// Per-day IndexedDB cache for Meta insights (Phase 3.99 — incremental sync).
// Schema: { date: 'YYYY-MM-DD', rows: [...], fetched_at: number }
// TTL: 1h for current day; infinite for past days.

import { openDB, STORE_DAILY } from './indexedDBStore';

const TTL_TODAY_MS = 60 * 60 * 1000; // 1h

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function listDaysInRange(since, until) {
  const days = [];
  const start = new Date(since + 'T00:00:00Z');
  const end = new Date(until + 'T00:00:00Z');
  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    days.push(d.toISOString().slice(0, 10));
  }
  return days;
}

export async function getDay(date) {
  const db = await openDB();
  return new Promise((res, rej) => {
    const tx = db.transaction([STORE_DAILY], 'readonly');
    const req = tx.objectStore(STORE_DAILY).get(date);
    req.onsuccess = () => res(req.result || null);
    req.onerror = () => rej(req.error);
  });
}

export async function setDay(date, rows) {
  const db = await openDB();
  return new Promise((res, rej) => {
    const tx = db.transaction([STORE_DAILY], 'readwrite');
    tx.objectStore(STORE_DAILY).put({ date, rows, fetched_at: Date.now() });
    tx.oncomplete = res;
    tx.onerror = (e) => rej(e.target.error);
  });
}

function isStale(entry) {
  if (!entry) return true;
  if (entry.date !== todayStr()) return false; // past days never go stale
  return (Date.now() - (entry.fetched_at || 0)) > TTL_TODAY_MS;
}

/**
 * Return the list of dates in [since, until] that are missing from cache
 * OR stale (today's entry older than TTL_TODAY_MS).
 */
export async function getMissingDays(since, until) {
  const all = listDaysInRange(since, until);
  const missing = [];
  for (const d of all) {
    const entry = await getDay(d);
    if (!entry || isStale(entry)) missing.push(d);
  }
  return missing;
}

/**
 * Return concatenated rows for [since, until] from cache (skips missing days).
 * Caller is expected to fetch missing days first via getMissingDays() and setDay().
 */
export async function getRowsForRange(since, until) {
  const all = listDaysInRange(since, until);
  const out = [];
  for (const d of all) {
    const entry = await getDay(d);
    if (entry && entry.rows) out.push(...entry.rows);
  }
  return out;
}

/**
 * Drop cache entries older than retention_days from today.
 */
export async function clearStale(retentionDays = 180) {
  const db = await openDB();
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - retentionDays);
  const cutoffStr = cutoff.toISOString().slice(0, 10);
  return new Promise((res, rej) => {
    const tx = db.transaction([STORE_DAILY], 'readwrite');
    const store = tx.objectStore(STORE_DAILY);
    const req = store.openCursor();
    req.onsuccess = (e) => {
      const cur = e.target.result;
      if (!cur) return;
      if (cur.value.date < cutoffStr) cur.delete();
      cur.continue();
    };
    tx.oncomplete = res;
    tx.onerror = (e) => rej(e.target.error);
  });
}

export { listDaysInRange };
